import { useNav } from "@pomle/react-router-paths";
import { ReactComponent as CheckIcon } from "assets/icons/check/check-24x24.svg";
import { ReactComponent as CheckIconBig } from "assets/icons/check/check-28x28.svg";
import { ReactComponent as ChevronIcon } from "assets/icons/chevron/chevron-24x24.svg";
import { ReactComponent as ExIcon } from "assets/icons/ex/ex-24x24.svg";
import { isOnboardingComplete } from "lib/onboarding/isOnboardingComplete";
import { findNotParticipatedStudies } from "lib/studies/findNotParticipatedStudies";
import { useCallback, useMemo } from "react";
import { useOnboardingQuery } from "render/hooks/api/queries/useOnboardingQuery";
import { usePatientConsentsQuery } from "render/hooks/api/queries/usePatientConsentsQuery";
import { useTracking } from "render/hooks/useTracking";
import { paths, queries } from "render/routes/paths";
import { useConsentsMap } from "render/views/ProfileView/components/DataAndPrivacySection/hooks/useConsentsMap";
import { appointmentEvents } from "../../tracking";
import { CompleteProfileButton } from "../CompleteProfileButton/";
import styles from "./styles.module.sass";
import { Trans } from "./trans";

export interface StepsSectionProps {
  slotId: string;
}

export function StepsSection({ slotId }: StepsSectionProps) {
  const { trackEvent } = useTracking();
  const nav = {
    appointmentOnboarding: useNav(
      paths.appointmentOnboarding,
      queries.onboarding
    ),
  };
  const onboardingQuery = useOnboardingQuery();
  const patientConsentsQuery = usePatientConsentsQuery();
  const { studies } = useConsentsMap();
  const isProfileComplete = useMemo(() => {
    if (!onboardingQuery.isFetched || patientConsentsQuery.data == null) {
      return true;
    }

    return isOnboardingComplete({
      patientConsents: patientConsentsQuery.data,
      onboardingData: onboardingQuery.data,
      requiredStudies: studies,
    });
  }, [
    onboardingQuery.data,
    onboardingQuery.isFetched,
    patientConsentsQuery.data,
    studies,
  ]);

  const allStudiesAgreed = useMemo(() => {
    if (patientConsentsQuery.data == null) {
      return false;
    }

    return (
      findNotParticipatedStudies(patientConsentsQuery.data, studies).length ===
      0
    );
  }, [patientConsentsQuery.data, studies]);

  const isOnboardingCompleted = useMemo(() => {
    return onboardingQuery.data != null;
  }, [onboardingQuery.data]);

  const openOnboardingOn = useCallback(
    (stage?: "studies" | "onboarding") => {
      const initialStep = [];
      if (stage != null) {
        initialStep.push(stage);
      }

      nav.appointmentOnboarding.go({ slotId: slotId }, { initialStep });
    },
    [nav.appointmentOnboarding, slotId]
  );

  const isOpen = onboardingQuery.isFetched && !!patientConsentsQuery.data;

  return (
    <div className={styles.stepsContainer} style={{ opacity: isOpen ? 1 : 0 }}>
      <div className={styles.onboarding}>
        <div className={styles.stepsHeader}>
          {isProfileComplete ? (
            <Trans.Title.Ready.Header />
          ) : (
            <Trans.Title.NotReady.Header />
          )}
        </div>
        <div className={styles.stepsDescription}>
          {isProfileComplete ? (
            <Trans.Title.Ready.SubHeader />
          ) : (
            <Trans.Title.NotReady.SubHeader />
          )}
        </div>
        <ul className={styles.todoList}>
          <li>
            <button
              data-completed={allStudiesAgreed}
              onClick={() => {
                trackEvent(
                  appointmentEvents.completeProfileStepClick("agree_to_studies")
                );
                openOnboardingOn("studies");
              }}
            >
              <div className={styles.iconContainer}>
                <div className={styles.circle} data-complete={allStudiesAgreed}>
                  <CheckIconBig />
                </div>
              </div>
              <div className={styles.todoText}>
                {allStudiesAgreed ? (
                  <Trans.TodoList.Done.RenewStudies />
                ) : (
                  <Trans.TodoList.Todo.RenewStudies />
                )}
              </div>
              <ChevronIcon className={styles.chevron} />
            </button>
          </li>

          <li>
            <button
              data-completed={isOnboardingCompleted}
              onClick={() => {
                trackEvent(
                  appointmentEvents.completeProfileStepClick("do_onboarding")
                );
                openOnboardingOn("onboarding");
              }}
            >
              <div className={styles.iconContainer}>
                <div
                  className={styles.circle}
                  data-complete={isOnboardingCompleted}
                >
                  <CheckIconBig />
                </div>
              </div>
              <div className={styles.todoText}>
                {isOnboardingCompleted ? (
                  <Trans.TodoList.Done.CompleteOnboarding />
                ) : (
                  <Trans.TodoList.Todo.CompleteOnboarding />
                )}
              </div>
              <ChevronIcon className={styles.chevron} />
            </button>
          </li>
        </ul>
        <div
          data-completed={isProfileComplete}
          className={styles.profileButton}
        >
          <CompleteProfileButton slotId={slotId} />
        </div>
      </div>
      <div className={styles.stepsTips}>
        <div className={styles.stepsTipsHeader}>
          <Trans.BeforeYourAppointment />
        </div>
        <ul className={styles.stepsTipsList}>
          <li>
            <CheckIcon />
            <Trans.CheckList.Eating />
          </li>
          <li>
            <CheckIcon />
            <Trans.CheckList.Exercises />
          </li>
          <li>
            <ExIcon />
            <Trans.CheckList.Caffeine />
          </li>
          <li>
            <ExIcon />
            <Trans.CheckList.Skin />
          </li>
        </ul>
      </div>
    </div>
  );
}
